/**
 * Heartbeat.js
 * Calls the Player Heartbeat API periodically to support Continue Watching functionality.
 */
class Heartbeat {
    
    constructor(options) {
        console.log('Simplestream::Heartbeat::Constructor()');
        
        // Validate options
        if(!options.videoElement) {
            console.error('Simplestream::Heartbeat: Missing videoElement');
            return;
        } else if(!options.companyId) {
            console.error('Simplestream::Heartbeat: Missing companyId');
            return;
        } else if(!options.contentId) {
            console.error('Simplestream::Heartbeat: Missing contentId');
            return;
        } else if(!options.contentType) {
            console.error('Simplestream::Heartbeat: Missing contentType');
            return;
        } else if(!options.url) {
            console.error('Simplestream::Heartbeat: Missing url');
            return;
        } else if(!options.userId) {
            console.error('Simplestream::Heartbeat: Missing userId');
        }
        
        this.validateOptions(options);
        
        this.videoElement = options.videoElement;
        this.companyId = options.companyId;
        this.baseUrl = options.url.endsWith('/') ? options.url : `${options.url}/`;
        this.contentId = options.contentId;
        this.contentType = options.contentType;
        this.userId = options.userId;
        
        this.sessionId = null;
        this.heartbeatInterval = null;
        this.heartbeatFrequency = 5000;
        
        this.handleVideoPlay = this.handleVideoPlay.bind(this);
        this.handleVideoEnd = this.handleVideoEnd.bind(this);
        
        this.init();
        
        if (this.videoElement && !this.videoElement.paused) {
            this.handleVideoPlay();
        }
    }

    /**
     * Validates that all required options are present
     */
    validateOptions(options) {
        var requiredOptions = [
            'videoElement',
            'companyId',
            'url',
            'contentId',
            'contentType',
            'userId'
        ];

        for (var i = 0; i < requiredOptions.length; i++) {
            var option = requiredOptions[i];
            if (!options[option]) {
                throw new Error('Missing required option: ' + option);
            }
        }
    }

    /**
     * Builds start session URL
     */
    getStartSessionUrl() {
        return this.baseUrl + 'start_session' +
            '?user_id=' + encodeURIComponent(this.userId) +
            '&company_id=' + encodeURIComponent(this.companyId) +
            '&content_id=' + encodeURIComponent(this.contentId) +
            '&content_type=' + encodeURIComponent(this.contentType) +
            '&platform_id=website' +
            '&play_head=' + Math.floor(this.videoElement?.currentTime || 0);
    }
    
    /**
     * Builds update session URL
     */
    getUpdateSessionUrl() {
        return this.baseUrl + 'update_session' +
            '?session_id=' + encodeURIComponent(this.sessionId) +
            '&company_id=' + encodeURIComponent(this.companyId) +
            '&play_head=' + Math.floor(this.videoElement?.currentTime || 0);
    }

    /**
     * Handles successful session start response
     */
    handleSessionStart(data) {
        this.sessionId = data.response.session_id;
        this.startHeartbeatInterval();
    }

    /**
     * Starts the heartbeat interval
     */
    startHeartbeatInterval() {
        this.stopHeartbeatInterval(); 
        
        this.heartbeatInterval = setInterval(function() {
            if (this.videoElement.paused) return;
            
            fetch(this.getUpdateSessionUrl())
                .then(function(response) {
                    if (!response.ok) {
                        throw new Error('Heartbeat update failed: ' + response.status);
                    }
                    return response.json();
                })
                .catch(function(error) {
                    console.error('Heartbeat update error:', error);
                }.bind(this));
        }.bind(this), this.heartbeatFrequency);
    }

    /**
     * Stops the heartbeat interval
     */
    stopHeartbeatInterval() {
        if (this.heartbeatInterval) {
            clearInterval(this.heartbeatInterval);
            this.heartbeatInterval = null;
        }
    }

    /**
     * Handles video play event
     */
    handleVideoPlay() {
        if (this.sessionId) return;
        
        console.log('Heartbeat: Video play detected, starting session');
        
        fetch(this.getStartSessionUrl())
            .then(function(response) {
                if (!response.ok) {
                    throw new Error('Failed to start session: ' + response.status);
                }
                return response.json();
            }.bind(this))
            .then(function(data) {
                console.log('Heartbeat: Session started successfully');
                this.handleSessionStart(data);
            }.bind(this))
            .catch(function(error) {
                console.error('Session start error:', error);
            });
    }

    /**
     * Handles video end event
     */
    handleVideoEnd() {
        this.stopHeartbeatInterval();
    }

    /**
     * Initializes video event listeners
     */
    init() {
        this.videoElement.addEventListener('play', this.handleVideoPlay);
        this.videoElement.addEventListener('playing', this.handleVideoPlay);
        this.videoElement.addEventListener('ended', this.handleVideoEnd);
        
        this.videoElement.addEventListener('seeked', function() {
            if (!this.videoElement.paused && !this.sessionId) {
                this.handleVideoPlay();
            }
        }.bind(this));
    }

    /**
     * Cleans up resources
     */
    destroy() {
        this.stopHeartbeatInterval();
        this.videoElement.removeEventListener('play', this.handleVideoPlay);
        this.videoElement.removeEventListener('playing', this.handleVideoPlay);
        this.videoElement.removeEventListener('ended', this.handleVideoEnd);
        this.sessionId = null;
    }
}

export default Heartbeat;
